import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Euro, Calendar, AlertTriangle, FileText, Clock, 
  Plus, ChevronRight, Shield, Phone, Car, Cloud, Zap, Printer, File
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const categoryIcons = {
  assurance: { icon: Shield, color: "text-blue-600", bg: "bg-blue-50", emoji: "🛡️" },
  telephonie: { icon: Phone, color: "text-purple-600", bg: "bg-purple-50", emoji: "📱" },
  leasing: { icon: Car, color: "text-red-600", bg: "bg-red-50", emoji: "🚗" },
  saas: { icon: Cloud, color: "text-cyan-600", bg: "bg-cyan-50", emoji: "☁️" },
  energie: { icon: Zap, color: "text-yellow-600", bg: "bg-yellow-50", emoji: "💡" },
  materiel: { icon: Printer, color: "text-gray-600", bg: "bg-gray-50", emoji: "🖨️" },
  autre: { icon: File, color: "text-slate-600", bg: "bg-slate-50", emoji: "📄" }
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount) return "0 €";
  return new Intl.NumberFormat("fr-FR", { 
    style: "currency", 
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
};

// Urgency badge component
const UrgencyBadge = ({ jours, urgence }) => {
  if (urgence === "depasse") {
    return <Badge className="bg-red-600 text-white">Dépassé</Badge>;
  }
  
  const colors = {
    critique: "bg-red-100 text-red-700 border-red-200",
    urgent: "bg-orange-100 text-orange-700 border-orange-200",
    attention: "bg-yellow-100 text-yellow-700 border-yellow-200",
    ok: "bg-green-100 text-green-700 border-green-200"
  };
  
  return (
    <Badge className={`${colors[urgence] || colors.ok} font-medium`}>
      <Clock className="w-3 h-3 mr-1" />
      {jours} jours
    </Badge>
  );
};

export default function EngagementsDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    kpis: {
      cout_mensuel_total: 0,
      cout_annuel_projete: 0,
      preavis_60_jours: 0,
      resiliations_en_cours: 0,
      total_contrats: 0
    },
    alertes_preavis: [],
    contrats_recents: []
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Erreur de chargement du tableau de bord");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Contrats & Engagements</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-slate-200 rounded w-3/4"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="engagements-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dépenses avec Engagements</h1>
          <p className="text-slate-500 mt-1">Gérez vos contrats récurrents et anticipez les résiliations</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate("/engagements/calendrier")}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Calendrier
          </Button>
          <Button onClick={() => navigate("/engagements/import")}>
            <Plus className="w-4 h-4 mr-2" />
            Importer
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="contrats" onClick={() => navigate("/engagements/contrats")}>
            Contrats ({data.kpis.total_contrats})
          </TabsTrigger>
          <TabsTrigger value="echeances" onClick={() => navigate("/engagements/calendrier")}>
            Échéances
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Coût mensuel</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {formatCurrency(data.kpis.cout_mensuel_total)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">HT / mois</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Euro className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Coût annuel</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {formatCurrency(data.kpis.cout_annuel_projete)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">projeté</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={data.kpis.preavis_60_jours > 0 ? "border-orange-200 bg-orange-50/30" : ""}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Préavis imminents</p>
                    <p className={`text-3xl font-bold mt-1 ${data.kpis.preavis_60_jours > 0 ? "text-orange-600" : "text-slate-900"}`}>
                      {data.kpis.preavis_60_jours}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">dans les 60 jours</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg ${data.kpis.preavis_60_jours > 0 ? "bg-orange-100" : "bg-slate-50"} flex items-center justify-center`}>
                    <AlertTriangle className={`w-6 h-6 ${data.kpis.preavis_60_jours > 0 ? "text-orange-600" : "text-slate-400"}`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={data.kpis.resiliations_en_cours > 0 ? "border-red-200 bg-red-50/30" : ""}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Résiliations ouvertes</p>
                    <p className={`text-3xl font-bold mt-1 ${data.kpis.resiliations_en_cours > 0 ? "text-red-600" : "text-slate-900"}`}>
                      {data.kpis.resiliations_en_cours}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">en cours</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg ${data.kpis.resiliations_en_cours > 0 ? "bg-red-100" : "bg-slate-50"} flex items-center justify-center`}>
                    <FileText className={`w-6 h-6 ${data.kpis.resiliations_en_cours > 0 ? "text-red-600" : "text-slate-400"}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alertes Préavis */}
          {data.alertes_preavis.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  <CardTitle className="text-lg">Alertes préavis</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.alertes_preavis.map((alerte) => {
                    const cat = categoryIcons[alerte.categorie] || categoryIcons.autre;
                    const Icon = cat.icon;
                    
                    return (
                      <div 
                        key={alerte.contrat_id}
                        className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/engagements/contrats/${alerte.contrat_id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg ${cat.bg} flex items-center justify-center`}>
                            <Icon className={`w-5 h-5 ${cat.color}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {alerte.prestataire} — {alerte.nom_contrat}
                            </p>
                            <p className="text-sm text-slate-500">
                              Préavis : {alerte.delai_preavis || "?"} • Résiliation : {formatDate(alerte.date_resiliation_possible)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-slate-900">
                              {formatCurrency(alerte.montant_mensuel_ht)}
                            </p>
                            <p className="text-xs text-slate-400">/mois</p>
                          </div>
                          <UrgencyBadge jours={alerte.jours_restants} urgence={alerte.urgence} />
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contrats récents */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Contrats récents</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/engagements/contrats")}>
                  Voir tout
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {data.contrats_recents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>Aucun contrat enregistré</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate("/engagements/import")}>
                    <Plus className="w-4 h-4 mr-2" />
                    Importer un contrat
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.contrats_recents.map((contrat) => {
                    const cat = categoryIcons[contrat.categorie] || categoryIcons.autre;
                    const Icon = cat.icon;
                    
                    return (
                      <div 
                        key={contrat.id}
                        className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/engagements/contrats/${contrat.id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg ${cat.bg} flex items-center justify-center`}>
                            <Icon className={`w-5 h-5 ${cat.color}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {contrat.prestataire} — {contrat.nom_contrat}
                            </p>
                            <p className="text-sm text-slate-500">
                              Expiration : {formatDate(contrat.date_echeance)} • {contrat.duree_engagement_mois || "?"} mois
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-slate-900">
                              {formatCurrency(contrat.montant_mensuel_ht)}
                            </p>
                            <p className="text-xs text-slate-400">/mois</p>
                          </div>
                          <Badge variant="outline" className={
                            contrat.statut === "resilier" ? "bg-red-50 text-red-700 border-red-200" :
                            contrat.urgence === "critique" ? "bg-red-50 text-red-700 border-red-200" :
                            contrat.urgence === "urgent" ? "bg-orange-50 text-orange-700 border-orange-200" :
                            "bg-green-50 text-green-700 border-green-200"
                          }>
                            {contrat.statut === "resilier" ? "Résilié" : "Actif"}
                          </Badge>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
